<!-- No Data Row -->
@if (data().length === 0 && !isRowsEditableInline()) {
  <div
    class="bg-elevated sticky left-0 flex w-[100cqw] flex-none flex-col items-center justify-center gap-2 px-14 py-10 text-center">
    <span
      class="border-weak inline-flex size-12 items-center justify-center rounded-lg border">
      <i [class]="ICONS.database" class="text-accent-main text-2xl"></i>
    </span>
    <p class="text-text-primary text-base font-semibold">
      {{ 'table.empty.title' | translate }}
    </p>
    <p class="text-text-tertiary max-w-xs text-xs font-normal">
      {{ 'table.empty.description' | translate }}
    </p>
  </div>
}

<cdk-virtual-scroll-viewport role="rowgroup" class="block">
  <div
    *cdkVirtualFor="let row of data(); trackBy: trackByFn; let i = index"
    [style]="tableColumnStyles()"
    role="row"
    class="group/row"
    (dblclick)="handleRowDoubleClick(row)"
    (click)="selectByRowClick(row)"
    (keydown.enter)="selectByRowClick(row)"
    [class]="getRowClasses(row, i)"
    [class.cursor-pointer]="isClickSelectable()"
    [attr.data-context]="getRowDataContext()"
    [attr.data-row-id]="tableId() ? tableId() + '-' + i : i"
    [attr.data-row-uid]="row[trackByKey()]">
    @if (config().rowNumbers) {
      <div
        role="cell"
        [class.border-l-2]="isRowHasError(row)"
        [class]="getPinnedCellClasses(row)"
        class="table-frozen-cell text-text-tertiary text-2xs border-l-danger border-weak flex items-center justify-center border-b p-2 font-mono transition-colors duration-200">
        @if (config().errors && isRowHasError(row)) {
          <i [class]="`${ICONS.warningCircle} text-danger`"></i>
        } @else {
          {{ i + 1 }}
        }
      </div>
    }

    @if (config().selection?.enabled && config().selection?.mode === 'checkbox') {
      <div
        role="cell"
        class="table-frozen-cell border-weak flex items-center justify-center border-b transition-colors duration-200"
        [class]="getPinnedCellClasses(row)"
        (mousedown)="onSelectionMouseDown($event)">
        <app-checkbox
          class="flex"
          size="sm"
          [checked]="isRowSelected(row)"
          (change)="handleSelectionChange(row)"
          [id]="(tableId() ? tableId() + '-' : '') + 'checkbox-' + i" />
      </div>
    }

    <!-- Data Cells -->
    @for (column of columns(); track column.key; let j = $index) {
      <div
        role="cell"
        [tabIndex]="j"
        [attr.data-column-key]="String(column.key)"
        class="border-weak text-text-primary h-full w-full border-b text-xs font-normal transition-all duration-200"
        [class.opacity-50]="isCellStale(row, column)"
        [class]="
          isRowSelected(row)
            ? 'bg-accent-interactive group-hover/row:bg-accent-interactive-hover'
            : ''
        ">
        @let cellOpts = getCellOptions(row, column);
        <app-table-cell
          class="contents"
          [dataType]="getCellDataType(column)"
          [allowEdit]="isRowsEditableInline() && !column.readonly"
          [errorForCell]="isCellHasError(row, column)"
          [value]="getCellValue(row, column)"
          [disabled]="cellOpts?.disabled"
          [bgColor]="cellOpts?.bgColor"
          [editorConfig]="getEffectiveEditorConfig(row, column)"
          [rowIndex]="i"
          [columnId]="String(column.key)"
          [unit]="column.unit"
          [displayLabelFor]="displayLabelForColumn(column)"
          [row]="row"
          [targetEntityType]="column.referenceEntityType"
          [filterable]="column.config?.filterable ?? false"
          [ownerEntityType]="ownerEntityType()"
          (uidClick)="onListItemClick(row, column, $event)"
          (editStart)="onCellEditStart(row)"
          (editEnd)="onCellEditEnd()"
          (editCommit)="onCellEditCommit($event, row, column)"
          (editNavigate)="onCellEditNavigate($event)" />
      </div>
    }

    <!-- Action Buttons -->
    @if (actions() && actions().length > 0) {
      <div
        role="cell"
        class="border-weak flex h-full items-center justify-center border-b px-2 py-1">
        <div
          class="row-actions duration-base flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100">
          @for (
            action of getVisibleActions(row);
            track trackActionByLabel($index, action)
          ) {
            <button
              #actionButton
              [disabled]="isActionDisabled(action, row)"
              [class]="getActionButtonClasses(action.color)"
              (click)="action.action(row)"
              (keydown.enter)="action.action(row)"
              [title]="action.label">
              @if (action.icon) {
                <i [class]="action.icon" class="text-sm leading-none"></i>
              }
            </button>
            <app-tooltip
              [target]="actionButton"
              [content]="action.label"
              [position]="'top'" />
          }
        </div>
      </div>
    }

    @if (config().columnActionSettings?.hideable && isThereHiddenColumns()) {
      <div role="cell" class="px-2 py-1"></div>
    }
  </div>
</cdk-virtual-scroll-viewport>

@if (isRowsEditableInline()) {
  <div role="row" [class]="getRowClasses()" [style]="tableColumnStyles()">
    <div
      role="cell"
      class="bg-surface group-hover:bg-interactive border-weak col-span-full cursor-pointer border-b transition-colors">
      <button
        class="text-accent-main sticky left-0 inline-flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs font-medium"
        (click)="addRow()">
        <i [class]="ICONS.plus" class="text-sm"></i>
        <span>{{ 'table.editable.add_row' | translate }}</span>
      </button>
    </div>
  </div>
}
